import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
  DestroyRef,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTableModule } from '@angular/material/table';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  merge,
  Observable,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  tap,
  timer,
} from 'rxjs';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import {
  CommunicationChannel,
  EventType,
  ListEventModel,
  SourceTypeEnum,
} from '@eagle/app/modules/events/models';
import { EventGroup } from '../timeline/models';
import { EventsApiService } from '@eagle/app/modules/events/services';
import { PersonNamePipe } from '../../pipes/person-name.pipe';
import { MatDialog } from '@angular/material/dialog';
import { SmsDialogComponent } from '../sms-dialog';
import moment from 'moment';
import { ToastService } from '../toast';
import { AuthService } from '../../services';
import { CurrentUserPayloadModel, UserRolesEnum } from '../../dto/current-user.model';
import { RouterModule } from '@angular/router';
import { Duration } from 'luxon';

@Component({
  selector: 'wlnob-unanswered-sms-list',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    TranslateModule,
    MatTooltipModule,
    MatIconModule,
    MatButtonModule,
    PersonNamePipe,
    RouterModule,
  ],
  templateUrl: './unanswered-sms-list.component.html',
  styleUrls: ['./unanswered-sms-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UnansweredSmsListComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  private eventsApiService = inject(EventsApiService);
  private dialog = inject(MatDialog);
  private toastService = inject(ToastService);
  private translateService = inject(TranslateService);
  private authService = inject(AuthService);

  private readonly reloadTrigger$ = new Subject<void>();
  private readonly reloadInterval = Duration.fromObject({ minutes: 30 }).as('milliseconds');
  private readonly autoReload$ = timer(0, this.reloadInterval);

  readonly reloadTriggerCombined$ = merge(this.reloadTrigger$, this.autoReload$);

  readonly isReloadingTable = signal(false);
  readonly displayedColumns = ['from', 'nr', 'received', 'actions'];
  readonly sourceTypeEnum = SourceTypeEnum;
  currentUser: CurrentUserPayloadModel;

  readonly unansweredSMS = output<number>();

  private readonly events$ = new BehaviorSubject<ListEventModel[]>([]);
  public eventGroups$ = this.events$.pipe(
    map(events => this.groupEvents(events)),
    shareReplay(1)
  );

  readonly filteredOfficeIds$: Observable<string[]> = this.authService.currentUser$.pipe(
    tap(currentUser => (this.currentUser = currentUser)),
    map(currentUser => {
      const allOffices = currentUser?.offices || [];
      const isFullAdmin = this.authService.hasRoles([
        UserRolesEnum.FULL_ADMIN,
        UserRolesEnum.SUPER_ADMIN,
        UserRolesEnum.SALES_ADMIN,
      ]);
      return isFullAdmin ? [] : allOffices.map(o => o.id);
    }),
    distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
  );

  readonly dataLoader$ = combineLatest([
    this.filteredOfficeIds$,
    this.reloadTriggerCombined$.pipe(startWith(void 0)),
  ]).pipe(
    tap(() => this.isReloadingTable.set(true)),
    switchMap(([officeIds]) => {
      const params = {
        officeId: officeIds,
        communicationChannel: CommunicationChannel.SMS,
        eventType: EventType.EMPLOYEE_SMS_NOTIFY,
        page: 0,
        size: 20,
        showOnlyUnansweredSMS: true,
      };
      return this.eventsApiService.getAll(params);
    }),
    tap(response => {
      this.unansweredSMS.emit(response.totalElements);

      this.events$.next(response.content);

      this.isReloadingTable.set(false);
    }),
    shareReplay(1)
  );

  ngOnInit(): void {
    this.dataLoader$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  private groupEvents(events: ListEventModel[]): EventGroup[] {
    return Object.entries(Object.groupBy(events, event => event.employee?.id)).map(
      ([_, events]) => {
        const first = events[0];
        return new EventGroup(first.eventType, events, true, first.employee);
      }
    );
  }

  triggerTableReload(): void {
    this.reloadTrigger$.next();
  }

  toggleRow(row: EventGroup) {
    row.collapsed = !row.collapsed;
  }

  sendSMS({ id, ...restEventData }: ListEventModel): void {
    this.dialog
      .open(SmsDialogComponent)
      .afterClosed()
      .pipe(
        filter<string>(Boolean),
        switchMap(message =>
          this.eventsApiService.create({
            ...restEventData,
            message,
            sourceType: SourceTypeEnum.CONSULTANT,
            dateTimeStart: moment().toISOString(),
            dateTimeEnd: moment().add(1, 'minute').toISOString(),
            authorUser: {
              id: this.currentUser?.user.id,
              firstName: this.currentUser?.user.firstName,
              lastName: this.currentUser?.user.lastName,
            },
          })
        )
      )
      .subscribe(() =>
        this.toastService.success([this.translateService.instant('FEEDBACK.SMS_SENT')])
      );
  }
}
